import React, { useState, FormEvent, useEffect, useCallback } from 'react';
import SessionModal from './SessionModal';
import GoogleCalendarIntegration from './GoogleCalendarIntegration';
import CVUpload from './CVUpload';
import CVManagement from './CVManagement';
import JobDescriptionUpload from './JobDescriptionUpload';
import CaseStudyUpload from './CaseStudyUpload';
import ProUpsell from './Billing/ProUpsell';
import { useAuth } from '../contexts/AuthContext';
import { INTERVIEW_TEMPLATES, getTemplateQuestions } from '../utils/interviewTemplates';
import {
  INTERVIEW_TYPE_LABELS,
  canUseInterviewType,
  getUserPlan,
  hasProFeatures,
} from '../utils/plans';

// For RAG operations (CV and Job Description), always use deployed API (Pinecone is configured there)
// This ensures RAG operations work even during local development
const RAG_API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface InterviewConfig {
  type?: string;
  difficulty?: string;
  templateId?: string;
  templateQuestions?: string[]; // Template-specific questions for the interview type
  isMultiSession?: boolean;
  interviews?: Array<{
    id: number;
    type: string;
    name: string;
    icon: string;
    duration: number;
    difficulty: string;
    order?: number;
  }>;
  totalDuration?: number;
  sessionId?: string;
  jobDescription?: string; // For focus_interview type
  caseStudyText?: string; // For case_study type
}

interface InterviewSetupProps {
  onStart: (config: InterviewConfig) => void;
  onViewHistory: () => void;
  onViewRecordings: () => void;
}

const InterviewSetup: React.FC<InterviewSetupProps> = ({
  onStart,
  onViewHistory,
  onViewRecordings,
}) => {
  const [interviewType, setInterviewType] = useState<string>('technical');
  const [difficulty, setDifficulty] = useState<string>('medium');
  const [templateId, setTemplateId] = useState<string>('');
  const [showSessionModal, setShowSessionModal] = useState<boolean>(false);
  const [showCalendarIntegration, setShowCalendarIntegration] =
    useState<boolean>(false);
  const [showCVUpload, setShowCVUpload] = useState<boolean>(false);
  const [showCVManagement, setShowCVManagement] = useState<boolean>(false);
  const [showJobDescriptionUpload, setShowJobDescriptionUpload] =
    useState<boolean>(false);
  const [showCaseStudyUpload, setShowCaseStudyUpload] =
    useState<boolean>(false);
  const [cvStatus, setCvStatus] = useState<{
    has_cv: boolean;
    vector_count?: number;
    message?: string;
  } | null>(null);
  const [jdStatus, setJdStatus] = useState<{
    has_job_description: boolean;
    vector_count?: number;
    message?: string;
  } | null>(null);
  const [csStatus, setCsStatus] = useState<{
    has_case_study: boolean;
    vector_count?: number;
    message?: string;
  } | null>(null);
  const [checkingCVStatus, setCheckingCVStatus] = useState<boolean>(false);
  const [checkingJDStatus, setCheckingJDStatus] = useState<boolean>(false);
  const [checkingCSStatus, setCheckingCSStatus] = useState<boolean>(false);
  const { user, getToken } = useAuth();
  const [upsellFeature, setUpsellFeature] = useState<string | null>(null);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';
  const plan = getUserPlan(user);
  const isPro = hasProFeatures(plan);
  const typeLocked = !canUseInterviewType(plan, interviewType);

  const proOnly = (feature: string, action: () => void) => () => {
    if (isPro) {
      action();
    } else {
      setUpsellFeature(feature);
    }
  };

  const checkCVStatus = useCallback(async () => {
    if (interviewType !== 'cv_review' || typeLocked) return;

    try {
      setCheckingCVStatus(true);
      const response = await fetch(
        `${RAG_API_URL}/cv/status?username=${encodeURIComponent(username)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCvStatus(data);
      }
    } catch (err) {
      console.error('Error checking CV status:', err);
      setCvStatus({ has_cv: false, message: 'Failed to check CV status' });
    } finally {
      setCheckingCVStatus(false);
    }
  }, [username, interviewType, typeLocked]);

  const checkJDStatus = useCallback(async () => {
    if (interviewType !== 'focus_interview' || typeLocked) return;

    try {
      setCheckingJDStatus(true);
      const response = await fetch(
        `${RAG_API_URL}/job-description/status?username=${encodeURIComponent(
          username
        )}`
      );
      if (response.ok) {
        const data = await response.json();
        setJdStatus(data);
      }
    } catch (err) {
      console.error('Error checking job description status:', err);
      setJdStatus({
        has_job_description: false,
        message: 'Failed to check status',
      });
    } finally {
      setCheckingJDStatus(false);
    }
  }, [username, interviewType, typeLocked]);

  const checkCSStatus = useCallback(async () => {
    if (interviewType !== 'case_study' || typeLocked) return;

    try {
      setCheckingCSStatus(true);
      const response = await fetch(
        `${RAG_API_URL}/case-study/status?username=${encodeURIComponent(username)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCsStatus(data);
      }
    } catch (err) {
      console.error('Error checking case study status:', err);
      setCsStatus({ has_case_study: false, message: 'Failed to check status' });
    } finally {
      setCheckingCSStatus(false);
    }
  }, [username, interviewType, typeLocked]);

  useEffect(() => {
    checkCVStatus();
    checkJDStatus();
    checkCSStatus();
  }, [checkCVStatus, checkJDStatus, checkCSStatus, interviewType]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (typeLocked) return;

    // Get template questions for the selected interview type
    const questions = templateId ? getTemplateQuestions(templateId, interviewType) : undefined;

    // Check if CV Review is selected but no CV uploaded
    if (interviewType === 'cv_review' && (!cvStatus || !cvStatus.has_cv)) {
      setShowCVUpload(true);
      return;
    }

    // Check if Focus Interview is selected
    if (interviewType === 'focus_interview') {
      if (jdStatus?.has_job_description) {
        onStart({
          type: 'focus_interview',
          difficulty: difficulty,
          templateId: templateId || undefined,
          templateQuestions: questions,
          isMultiSession: false,
        });
        return;
      }
      setShowJobDescriptionUpload(true);
      return;
    }

    // Check if Case Study is selected
    if (interviewType === 'case_study') {
      if (csStatus?.has_case_study) {
        onStart({
          type: 'case_study',
          difficulty: difficulty,
          templateId: templateId || undefined,
          templateQuestions: questions,
          isMultiSession: false,
        });
        return;
      }
      setShowCaseStudyUpload(true);
      return;
    }

    onStart({
      type: interviewType,
      difficulty: difficulty,
      templateId: templateId || undefined,
      templateQuestions: questions,
      isMultiSession: false,
    });
  };

  const handleStartSession = (sessionConfig: InterviewConfig) => {
    setShowSessionModal(false);
    onStart(sessionConfig);
  };

  return (
    <div className="flex justify-center items-center min-h-screen p-4 sm:p-5">
      <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 max-w-2xl w-full shadow-xl border border-slate-200">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl text-slate-800 mb-2 text-center font-bold">
          AI Interview Practice
        </h1>
        <p className="text-center text-slate-500 mb-6 sm:mb-8 text-base sm:text-lg">
          Practice system design, coding, and technical Q&A with AI
        </p>

        <form onSubmit={handleSubmit} className="mb-8">
          <div className="mb-6">
            <label
              htmlFor="template"
              className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base"
            >
              Interview Template (Optional)
            </label>
            <select
              id="template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full min-w-0 px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-xs sm:text-sm md:text-base transition-colors focus:outline-none focus:border-indigo-500 bg-white pr-8 sm:pr-10 md:pr-12"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundSize: '1em 1em',
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
              }}
            >
              <option value="">None (General Interview)</option>
              {INTERVIEW_TEMPLATES.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.icon} {template.name}
                </option>
              ))}
            </select>
            {templateId && (
              <p className="mt-2 text-xs sm:text-sm text-slate-500 break-words">
                <span className="font-medium">
                  {INTERVIEW_TEMPLATES.find((t) => t.id === templateId)?.icon}{' '}
                  {INTERVIEW_TEMPLATES.find((t) => t.id === templateId)?.name}
                </span>
                <span className="block sm:inline sm:ml-1">
                  {' '}
                  -{' '}
                  {
                    INTERVIEW_TEMPLATES.find((t) => t.id === templateId)
                      ?.description
                  }
                </span>
              </p>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="interview-type"
              className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base"
            >
              Interview Type
            </label>
            <select
              id="interview-type"
              value={interviewType}
              onChange={(e) => setInterviewType(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:border-indigo-500 bg-white pr-10 sm:pr-12"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundSize: '1.5em 1.5em',
                backgroundPosition: 'right 0.75rem center',
                backgroundRepeat: 'no-repeat',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
              }}
            >
              {Object.entries(INTERVIEW_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {canUseInterviewType(plan, value) ? label : `${label} · Pro 🔒`}
                </option>
              ))}
            </select>
            {templateId && (
              <p className="mt-2 text-xs text-slate-500">
                Questions will be tailored to:{' '}
                {INTERVIEW_TEMPLATES.find((t) => t.id === templateId)?.name}
              </p>
            )}

            {typeLocked && (
              <ProUpsell feature={INTERVIEW_TYPE_LABELS[interviewType] || 'This interview'} />
            )}

            {/* CV Status Indicator for CV Review */}
            {interviewType === 'cv_review' && !typeLocked && (
              <div className="mt-3">
                {checkingCVStatus ? (
                  <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-600 text-xs">
                      <div className="w-3 h-3 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin"></div>
                      <span>Checking CV status...</span>
                    </div>
                  </div>
                ) : cvStatus ? (
                  cvStatus.has_cv ? (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 text-sm">✓</span>
                        <div className="flex-1">
                          <p className="text-green-800 text-xs font-medium m-0 mb-1">
                            CV uploaded and ready ({cvStatus.vector_count || 0}{' '}
                            sections indexed)
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowCVUpload(true)}
                            className="mt-2 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Update CV
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-600 text-sm">⚠</span>
                        <div className="flex-1">
                          <p className="text-amber-800 text-xs font-medium m-0 mb-1">
                            No CV uploaded
                          </p>
                          <p className="text-amber-700 text-xs m-0">
                            You need to upload a CV before starting a CV Review
                            interview.
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowCVUpload(true)}
                            className="mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            Upload CV Now
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            )}

            {/* Focus Interview Status Indicator */}
            {interviewType === 'focus_interview' && !typeLocked && (
              <div className="mt-3">
                {checkingJDStatus ? (
                  <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-600 text-xs">
                      <div className="w-3 h-3 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin"></div>
                      <span>Checking job description status...</span>
                    </div>
                  </div>
                ) : jdStatus?.has_job_description ? (
                  <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-green-600 text-sm">✓</span>
                      <div className="flex-1">
                        <p className="text-green-800 text-xs font-medium m-0 mb-1">
                          Job description ready ({jdStatus.vector_count || 0}{' '}
                          sections indexed)
                        </p>
                        <p className="text-green-700 text-xs m-0">
                          Click "Start Interview" to continue with existing JD.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowJobDescriptionUpload(true)}
                          className="mt-2 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Update Job Description
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 text-sm">⚠</span>
                      <div className="flex-1">
                        <p className="text-amber-800 text-xs font-medium m-0 mb-1">
                          No job description uploaded
                        </p>
                        <p className="text-amber-700 text-xs m-0">
                          You need to upload a job description before starting a
                          Focus Interview.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowJobDescriptionUpload(true)}
                          className="mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          Upload Job Description
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Case Study Status Indicator */}
            {interviewType === 'case_study' && !typeLocked && (
              <div className="mt-3">
                {checkingCSStatus ? (
                  <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 text-slate-600 text-xs">
                      <div className="w-3 h-3 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
                      <span>Checking case study status...</span>
                    </div>
                  </div>
                ) : csStatus?.has_case_study ? (
                  <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-green-600 text-sm">✓</span>
                      <div className="flex-1">
                        <p className="text-green-800 text-xs font-medium m-0 mb-1">
                          Case study ready ({csStatus.vector_count || 0}{' '}
                          sections indexed)
                        </p>
                        <p className="text-green-700 text-xs m-0">
                          Click &quot;Start Interview&quot; to continue with existing case study.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowCaseStudyUpload(true)}
                          className="mt-2 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Update Case Study
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 text-sm">⚠</span>
                      <div className="flex-1">
                        <p className="text-amber-800 text-xs font-medium m-0 mb-1">
                          No case study uploaded
                        </p>
                        <p className="text-amber-700 text-xs m-0">
                          You need to upload a case study before starting.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowCaseStudyUpload(true)}
                          className="mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          Upload Case Study
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="difficulty"
              className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base"
            >
              Difficulty Level
            </label>
            <select
              id="difficulty"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:border-indigo-500 bg-white pr-10 sm:pr-12"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundSize: '1.5em 1.5em',
                backgroundPosition: 'right 0.75rem center',
                backgroundRepeat: 'no-repeat',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
              }}
            >
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-2">
            <button
              type="submit"
              disabled={typeLocked}
              className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-indigo-600 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              Start Interview
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-emerald-600 hover:to-teal-700 hover:-translate-y-0.5 hover:shadow-xl"
              onClick={proOnly('Multi-stage sessions', () => setShowSessionModal(true))}
            >
              🎯 Start Session{!isPro && ' · Pro'}
            </button>
          </div>
        </form>

        <div className="flex flex-col gap-2 mt-4">
          <button
            className="w-full px-3 py-3 bg-transparent border-2 border-slate-200 rounded-lg text-slate-500 text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 hover:text-indigo-500 hover:bg-indigo-50"
            onClick={onViewHistory}
          >
            📊 View Interview History
          </button>
          <button
            className="w-full px-3 py-3 bg-transparent border-2 border-slate-200 rounded-lg text-slate-500 text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 hover:text-indigo-500 hover:bg-indigo-50"
            onClick={proOnly('Interview recordings', onViewRecordings)}
          >
            🎙️ View Recordings{!isPro && ' · Pro'}
          </button>
          <button
            className="w-full px-3 py-3 bg-transparent border-2 border-slate-200 rounded-lg text-slate-500 text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 hover:text-indigo-500 hover:bg-indigo-50"
            onClick={proOnly('Calendar scheduling', () => setShowCalendarIntegration(true))}
          >
            📅 Schedule Interview (Google Calendar){!isPro && ' · Pro'}
          </button>
          <button
            className="w-full px-3 py-3 bg-transparent border-2 border-slate-200 rounded-lg text-slate-500 text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 hover:text-indigo-500 hover:bg-indigo-50"
            onClick={proOnly('CV management', () => setShowCVManagement(true))}
          >
            📄 Manage CV{!isPro && ' · Pro'}
          </button>
          {upsellFeature && (
            <ProUpsell feature={upsellFeature} onClose={() => setUpsellFeature(null)} />
          )}
        </div>

        <div className="border-t-2 border-slate-200 pt-6 mt-8">
          <h3 className="text-slate-800 mb-4 font-semibold text-lg">
            Features
          </h3>
          <ul className="list-none p-0">
            <li className="py-2 text-slate-600">
              🎤 Voice input and AI voice responses
            </li>
            <li className="py-2 text-slate-600">
              🖥️ Real-time screen analysis
            </li>
            <li className="py-2 text-slate-600">
              💬 Live feedback and follow-up questions
            </li>
            <li className="py-2 text-slate-600">
              📊 Real-time code/design analysis
            </li>
            <li className="py-2 text-slate-600">
              🎯 Multi-interview sessions with timed stages
            </li>
          </ul>
        </div>
      </div>

      <SessionModal
        isOpen={showSessionModal}
        onClose={() => setShowSessionModal(false)}
        onStart={(sessionConfig) => {
          handleStartSession({
            ...sessionConfig,
            templateId: templateId || undefined,
          });
        }}
      />

      {showCalendarIntegration && user && (
        <GoogleCalendarIntegration
          user={user}
          onClose={() => setShowCalendarIntegration(false)}
          getToken={getToken}
          interviewConfig={{
            type: interviewType,
            difficulty: difficulty,
            templateId: templateId || undefined,
          }}
        />
      )}

      {showCVUpload && user && (
        <CVUpload
          user={user}
          getToken={getToken}
          onClose={() => setShowCVUpload(false)}
          onSuccess={() => {
            setShowCVUpload(false);
            checkCVStatus();
          }}
        />
      )}

      {showCVManagement && user && (
        <div className="fixed inset-0 z-[10000]">
          <CVManagement
            user={user}
            getToken={getToken}
            onBack={() => setShowCVManagement(false)}
          />
        </div>
      )}

      {showJobDescriptionUpload && user && (
        <JobDescriptionUpload
          user={user}
          getToken={getToken}
          onClose={() => setShowJobDescriptionUpload(false)}
          onSuccess={(jobDescription) => {
            setShowJobDescriptionUpload(false);
            checkJDStatus();
            onStart({
              type: 'focus_interview',
              difficulty: difficulty,
              templateId: templateId || undefined,
              isMultiSession: false,
              jobDescription: jobDescription,
            });
          }}
        />
      )}

      {showCaseStudyUpload && user && (
        <CaseStudyUpload
          user={user}
          getToken={getToken}
          onClose={() => setShowCaseStudyUpload(false)}
          onSuccess={(caseStudyText) => {
            setShowCaseStudyUpload(false);
            checkCSStatus();
            onStart({
              type: 'case_study',
              difficulty: difficulty,
              templateId: templateId || undefined,
              isMultiSession: false,
              caseStudyText: caseStudyText,
            });
          }}
        />
      )}
    </div>
  );
};

export default InterviewSetup;
